import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Flame, TrendingUp, BarChart3 } from 'lucide-react'

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center border text-[12px] leading-[16px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground",
        sell: "border-[#ff540c] bg-[rgba(255,255,255,0.4)] dark:bg-[rgba(28,28,28,0.4)] text-[#ff540c] h-full px-[24px] py-[2px] rounded-none",
        buy: "border-[#1fb845] bg-[rgba(255,255,255,0.4)] dark:bg-[rgba(28,28,28,0.4)] text-[#1fb845] h-full px-[24px] py-[2px] rounded-none",
        equity: "border-transparent bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400",
        liquid_token: "border-transparent bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400",
        saft: "border-transparent bg-purple-500/10 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400",
        safe: "border-transparent bg-orange-500/10 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400",
        token: "border-transparent bg-purple-500/10 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400",
        unknown: "border-transparent bg-gray-500/10 text-gray-600 dark:bg-gray-500/20 dark:text-gray-400",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export interface TagBadgeProps {
  tag?: 'hot' | 'trending' | 'most_profitable'
}

export const TagBadge = ({ tag }: TagBadgeProps) => {
  if (!tag) return null

  const tagConfig = {
    hot: {
      icon: <Flame className="w-3.5 h-3.5 text-[#ff0eb7]" />,
      label: 'Hot',
      textColorClass: 'text-[#ff0eb7]'
    },
    trending: {
      icon: <TrendingUp className="w-3.5 h-3.5 text-[#0e4fff] dark:text-[#39ff14]" />,
      label: 'Trending',
      textColorClass: 'text-[#0e4fff] dark:text-[#39ff14]'
    },
    most_profitable: {
      icon: <BarChart3 className="w-3.5 h-3.5 text-[#24b60d]" />,
      label: 'Most profitable',
      textColorClass: 'text-[#24b60d]'
    }
  }

  const config = tagConfig[tag]

  return (
    <div className="bg-white dark:bg-transparent border border-[rgba(0,0,0,0.24)] dark:border-[rgba(253,253,253,0.24)] flex items-center gap-1 pl-1.5 pr-[7px] py-1.5 font-plus-jakarta">
      {config.icon}
      <span className={cn("text-xs leading-4 font-semibold", config.textColorClass)}>
        {config.label}
      </span>
    </div>
  )
}

// Mobile version of TagBadge with specific sizes from Figma design
export const TagBadgeMobile = ({ tag }: TagBadgeProps) => {
  if (!tag) return null

  const tagConfig = {
    hot: {
      icon: <Flame className="w-[14px] h-[14px]" style={{ color: '#ff0eb7' }} />,
      label: 'Hot',
      textColor: '#ff0eb7',
      bgColor: 'bg-white dark:bg-transparent',
      borderColor: 'border border-[rgba(0,0,0,0.24)] dark:border-[rgba(253,253,253,0.24)]'
    },
    trending: {
      icon: <TrendingUp className="w-[14px] h-[14px]" style={{ color: 'white' }} />,
      label: 'Trending',
      textColor: 'white',
      bgColor: 'bg-[#0e4fff]',
      borderColor: 'border-0'
    },
    most_profitable: {
      icon: <BarChart3 className="w-[14px] h-[14px]" style={{ color: 'white' }} />,
      label: 'Most profitable',
      textColor: 'white',
      bgColor: 'bg-[#24b60d]',
      borderColor: 'border-0'
    }
  }

  const config = tagConfig[tag]

  return (
    <div className={`${config.bgColor} ${config.borderColor} flex items-center gap-[4px] pl-[6px] pr-[7px] py-[6px] font-plus-jakarta`}>
      {config.icon}
      <span className="font-plus-jakarta font-semibold text-[12px] leading-[16px]" style={{ color: config.textColor }}>
        {config.label}
      </span>
    </div>
  )
}

export { Badge, badgeVariants }
